import {StyleSheet} from "aphrodite";
import gql from "graphql-tag";

import {color} from "@khanacademy/wonder-blocks-tokens";
import {HeadingXSmall} from "@khanacademy/wonder-blocks-typography";
import type {TrackedPageSlug} from "@repo/analytics-performance";
import {useQuery} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";

import useProfile from "#data-access/use-profile.ts";

import type {homepageQueryV4_user_userDistrictInfos as GqlUserDistrictInfo} from "../__graphql-types__/homepageQueryV4.ts";
import {
    getClassHasActiveAssignment,
    type HomepageQuery,
    type SideNavClass,
} from "../homepage-data.ts";

import type {
    ActiveAssignmentsAndGoalsQuery,
    ActiveAssignmentsAndGoalsQueryVariables,
} from "./__graphql-types__/ActiveAssignmentsAndGoalsQuery.ts";
import Assignments from "./assignments.tsx";
import CoursesForClass from "./courses-for-class.tsx";
import Goals from "./goals.tsx";

export const classDueDatesQuery: GqlOperation<
    ActiveAssignmentsAndGoalsQuery,
    ActiveAssignmentsAndGoalsQueryVariables
> = gqlOp(gql`
    query ActiveAssignmentsAndGoalsQuery(
        $classDescriptor: String!
        $dueAfter: DateTime!
        $userKaid: String!
    ) {
        user(kaid: $userKaid) {
            id
            isKmapStudent
            assignmentsPage(
                classroomDescriptor: $classDescriptor
                dueAfter: $dueAfter
                orderBy: DUE_DATE_ASC
            ) {
                assignments {
                    id
                    dueDate
                }
            }
            masteryAssignments(
                classroomDescriptor: $classDescriptor
                isPast: false
            ) {
                id
                dueDate
            }
        }
    }
`);

// Query for the due dates of non-past assignments and non-past (course and unit) mastery goals
const useClassDueDates = (classDescriptor: string, userKaid: string) => {
    // Get the current time, but round it down to the start of the hour
    // so that the value doesn't change on every render, triggering a refetch
    // of the query.
    const startOfHourDate = new Date();
    startOfHourDate.setMinutes(0, 0, 0);

    const [result] = useQuery(classDueDatesQuery, {
        variables: {
            classDescriptor,
            dueAfter: startOfHourDate.toISOString(),
            userKaid: userKaid,
        },
    });
    return result;
};

type Props = {
    classData: SideNavClass;
    query: HomepageQuery;
    refetch: () => void;
    curriedGetWelcomeState: (classId: string, coachKaid: string) => any;
    sufficientlyLoaded: (key: TrackedPageSlug) => void;
    userDistrictInfo?: GqlUserDistrictInfo | null;
};

export const ClassDetails = ({
    classData,
    query,
    refetch,
    curriedGetWelcomeState,
    sufficientlyLoaded,
    userDistrictInfo,
}: Props): JSX.Element | null => {
    const {coachKaid, classId, classroomDescriptor} = classData;

    // Query for the user's KMAP status, assignments, and mastery goals for the class
    const profileResult = useProfile();
    const userKaid =
        profileResult.status === "success"
            ? profileResult.profile?.get("kaid") || ""
            : "";
    const classDueDates = useClassDueDates(classroomDescriptor, userKaid);

    // User's KMAP status
    const hasLearningPathGoals = classData.hasMapGoals;
    const isKmapStudent =
        classDueDates.status === "success" &&
        classDueDates.data.user?.isKmapStudent;

    // User's current assignments (not past due)
    const assignments =
        classDueDates.status === "success"
            ? classDueDates.data.user?.assignmentsPage?.assignments
            : null;
    const hasAssignments = assignments && assignments.length > 0;

    // User's current mastery goals (not past due)
    const masteryGoals =
        classDueDates.status === "success"
            ? classDueDates.data?.user?.masteryAssignments
            : null;
    const hasMasteryGoals = masteryGoals && masteryGoals.length > 0;
    const welcomeState = curriedGetWelcomeState(classId, coachKaid);

    const renderAssignments = () => (
        <Assignments
            kaid={userKaid}
            classroomDescriptor={classroomDescriptor}
            hasActiveAssignments={getClassHasActiveAssignment(
                classId,
                coachKaid,
                query,
            )}
            loading={query.loading}
            refetchHomepageQuery={refetch}
            onSufficientlyUsable={() => sufficientlyLoaded("lihp_assignments")}
        />
    );
    const renderGoals = () => (
        <Goals
            classTitle={classData.className}
            kaid={userKaid}
            coachKaid={coachKaid}
            classId={classId}
            classDescriptor={classroomDescriptor}
            welcomeState={welcomeState}
            isMap={false}
            onSufficientlyUsable={() => sufficientlyLoaded("lihp_course_goals")}
        />
    );

    return (
        <>
            <HeadingXSmall tag="h2" style={styles.classTitle}>
                {classData.className}
            </HeadingXSmall>
            {hasLearningPathGoals && isKmapStudent && (
                <Goals
                    classTitle={classData.className}
                    kaid={userKaid}
                    coachKaid={coachKaid}
                    classId={classId}
                    classDescriptor={classroomDescriptor}
                    welcomeState={welcomeState}
                    isMap={true}
                    onSufficientlyUsable={() =>
                        sufficientlyLoaded("lihp_kmap_goals")
                    }
                    currentAssessmentTestKey={
                        userDistrictInfo?.currentMathAssessment?.mapTestKey ||
                        undefined
                    }
                    gradeLevel={userDistrictInfo?.gradeLevel || undefined}
                />
            )}
            {hasAssignments && renderAssignments()}
            {hasMasteryGoals && renderGoals()}
            <CoursesForClass
                classDescriptor={classroomDescriptor}
                courseIds={classData.courseIds ?? null}
            />
            {!hasAssignments && renderAssignments()}
            {!hasMasteryGoals && renderGoals()}
        </>
    );
};

const styles = StyleSheet.create({
    classTitle: {
        color: color.offBlack64,
    },
});
